import { useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useModalLayer } from './useModalLayer'
import { closeActivePeek } from './peekStore'
import { isActionMenuOpen } from '../../lib/openMenus'

/**
 * The shared full-screen modal: portal, scroll lock, focus trap, focus
 * restore, backdrop close, and TOPMOST-ONLY keyboard handling.
 *
 * Extracted from CardLightbox (plan 2026-10-07, section 7) so the combo
 * viewer and the lightbox it opens stack correctly:
 *
 * - Portal to <body>, so an ancestor's transform never becomes the
 *   containing block of the fixed overlay.
 * - The SHARED ref-counted scroll lock (src/lib/scrollLock.ts), so the body
 *   stays locked until the last overlay closes.
 * - Keys go through the modal stack (./modalStack.ts): ONE document listener
 *   hands each key to the top modal only. Escape closes it, Tab is trapped in
 *   it, and anything else (the lightbox's arrow keys) goes to `onKey`. A
 *   modal never listens on window by itself.
 * - Focus moves in on open and back to the opener on close (or to
 *   `restoreFocus`, when the opener wants to take it back quietly).
 * - A click on the backdrop itself closes. Every click is stopped at the
 *   root: the portal still bubbles through the React tree, and a click in a
 *   modal must never reach the row or name that opened it.
 * - Opening closes any card peek first: the peek sits above modals (z-90)
 *   and must not float over the thing that replaced it.
 */
export default function ModalShell({
  onClose,
  label,
  className,
  onKey,
  initialFocusRef,
  restoreFocus,
  children,
}: {
  onClose: () => void
  /** Accessible name of the dialog. */
  label: string
  /** Classes for the full-screen root, including its z-index. */
  className: string
  /** Keys other than Escape and Tab, delivered only while this modal is the
   *  top of the stack. */
  onKey?: (e: KeyboardEvent) => void
  /** Where focus goes on open; the root itself when absent. */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Replaces the default "focus the opener again" on close. */
  restoreFocus?: () => void
  children: ReactNode
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  // Latest closures in refs: the layer registers once per mount.
  const onCloseRef = useRef(onClose)
  const onKeyRef = useRef(onKey)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
    onKeyRef.current = onKey
  })

  // The shared modal stack (useModalLayer): scroll lock, focus in and back,
  // top-only keys, covered layers inert, later layers above.
  useModalLayer(true, {
    root: () => rootRef.current,
    onOpen: closeActivePeek,
    initialFocus: () => initialFocusRef?.current,
    restoreFocus,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A menu portalled above this modal (the Buy from picker) takes the
        // Escape and closes itself; the modal stays, as in useModalFocus.
        if (isActionMenuOpen()) return
        e.preventDefault()
        onCloseRef.current()
        return
      }
      if (e.key === 'Tab') {
        trapTab(e, rootRef.current)
        return
      }
      onKeyRef.current?.(e)
    },
  })

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      className={className}
      onClick={e => {
        e.stopPropagation()
        if (e.target === e.currentTarget) onCloseRef.current()
      }}
    >
      {children}
    </div>,
    document.body,
  )
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Keep Tab cycling inside `root`. The root itself (tabIndex -1, focused on
 *  open) is not in the cycle but counts as a boundary, so Shift+Tab cannot
 *  escape from it. */
function trapTab(e: KeyboardEvent, root: HTMLElement | null): void {
  if (!root) return
  const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (focusables.length === 0) {
    e.preventDefault()
    root.focus()
    return
  }
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement
  // Where focus is NOW, after any handler that ran before this one. This
  // listener sits on the document, so every handler on an element (and
  // React's, which run at the root or portal container) has already run.
  // An open card peek's panel is portaled outside this root but belongs to
  // the trigger inside it, so focus there is still inside the modal.
  // eslint-disable-next-line i18next/no-literal-string -- a CSS selector
  const inPeek = active instanceof Element && !!active.closest('[data-card-peek]')
  const inside = active instanceof Node && root.contains(active)
  // Another handler took this Tab (a peek trigger sending focus into its
  // panel). Honor that ONLY when focus stayed in this modal's region; a
  // handler that moved focus outside it does not get to escape the trap.
  if (e.defaultPrevented && (inside || inPeek)) return
  if (inPeek) return
  if (e.shiftKey && (active === first || active === root || !root.contains(active))) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && (active === last || active === root || !root.contains(active))) {
    e.preventDefault()
    first.focus()
  }
}
